import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, PhoneIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage, Language } from '../contexts/LanguageContext';
const languages: {
  code: Language;
  label: string;
}[] = [{
  code: 'es',
  label: 'ES'
}, {
  code: 'ca',
  label: 'CA'
}, {
  code: 'eu',
  label: 'EU'
}, {
  code: 'gl',
  label: 'GL'
}, {
  code: 'en',
  label: 'EN'
}];
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLangMenuOpen(false);
  }, [location]);
  // Close language menu when mobile menu opens, and vice versa
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsLangMenuOpen(false);
    }
  }, [isMobileMenuOpen]);
  useEffect(() => {
    if (isLangMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isLangMenuOpen]);
  const navLinks = [{
    path: '/',
    label: t('nav.home')
  }, {
    path: '/construccion',
    label: t('nav.construction')
  }, {
    path: '/reformas',
    label: t('nav.renovations')
  }];
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`} data-id="element-166">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-167">
          <div className="flex items-center justify-between h-14 sm:h-16" data-id="element-168">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-id="element-169">
              <motion.span className={`text-base sm:text-lg font-semibold tracking-tight transition-colors duration-300 ${isScrolled ? 'text-black' : 'text-white drop-shadow-md'}`} whileHover={{
              scale: 1.01
            }} data-id="element-170">
                HABITALUM
              </motion.span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-5 lg:gap-6" data-id="element-171">
              {navLinks.map(link => <Link key={link.path} to={link.path} className={`relative text-sm font-normal transition-colors duration-300 py-1.5 ${isScrolled ? 'text-neutral-600 hover:text-black' : 'text-white/85 hover:text-white'}`} data-id="element-172">
                  {link.label}
                  {location.pathname === link.path && <motion.div layoutId="navbar-indicator" className={`absolute -bottom-0.5 left-0 right-0 h-px rounded-full ${isScrolled ? 'bg-black' : 'bg-white'}`} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-173" />}
                </Link>)}

              {/* Language Selector */}
              <div className="relative" data-id="element-174">
                <motion.button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm font-normal transition-all duration-300 ${isScrolled ? 'text-neutral-600 hover:bg-neutral-100' : 'text-white/85 hover:bg-white/10'}`} whileTap={{
                scale: 0.98
              }} data-id="element-175">
                  <span data-id="element-176">{language.toUpperCase()}</span>
                  <motion.div animate={{
                  rotate: isLangMenuOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-177">
                    <ChevronDownIcon className="w-3 h-3" data-id="element-178" />
                  </motion.div>
                </motion.button>

                <AnimatePresence data-id="element-179">
                  {isLangMenuOpen && <>
                      <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="fixed inset-0 z-40" onClick={() => setIsLangMenuOpen(false)} data-id="element-180" />
                      <motion.div initial={{
                    opacity: 0,
                    y: 8,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 8,
                    scale: 0.96
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute right-0 top-full mt-1.5 bg-white rounded-lg shadow-xl shadow-black/10 border border-neutral-100 overflow-hidden z-50 min-w-[70px]" data-id="element-181">
                        {languages.map((lang, index) => <motion.button key={lang.code} onClick={() => {
                      setLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }} className={`w-full flex items-center justify-center px-3 py-2 text-sm transition-all duration-200 ${language === lang.code ? 'bg-black text-white font-medium' : 'text-neutral-600 hover:bg-neutral-50'}`} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: index * 0.03
                    }} data-id="element-182">
                            {lang.label}
                          </motion.button>)}
                      </motion.div>
                    </>}
                </AnimatePresence>
              </div>

              <motion.a href="tel:+34631012592" className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${isScrolled ? 'bg-black text-white hover:bg-neutral-800' : 'bg-white text-black hover:bg-neutral-100'}`} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-183">
                <PhoneIcon className="w-3.5 h-3.5" data-id="element-184" />
                <span className="hidden lg:inline" data-id="element-185">{t('nav.call')}</span>
              </motion.a>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center gap-2 md:hidden" data-id="element-186">
              <motion.button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${isScrolled ? 'text-black bg-neutral-100' : 'text-white bg-white/15'}`} whileTap={{
              scale: 0.95
            }} data-id="element-187">
                {language.toUpperCase()}
              </motion.button>

              <motion.button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`p-1.5 rounded-md transition-colors ${isScrolled ? 'text-black' : 'text-white'}`} whileTap={{
              scale: 0.95
            }} data-id="element-188">
                <AnimatePresence mode="wait" data-id="element-189">
                  {isMobileMenuOpen ? <motion.div key="close" initial={{
                  rotate: -90,
                  opacity: 0
                }} animate={{
                  rotate: 0,
                  opacity: 1
                }} exit={{
                  rotate: 90,
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} data-id="element-190">
                      <XIcon className="w-5 h-5" data-id="element-191" />
                    </motion.div> : <motion.div key="menu" initial={{
                  rotate: 90,
                  opacity: 0
                }} animate={{
                  rotate: 0,
                  opacity: 1
                }} exit={{
                  rotate: -90,
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} data-id="element-192">
                      <MenuIcon className="w-5 h-5" data-id="element-193" />
                    </motion.div>}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Language Menu */}
      <AnimatePresence data-id="element-194">
        {isLangMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="fixed inset-x-0 top-14 z-50 md:hidden px-4" data-id="element-195">
            <div className="bg-white shadow-lg rounded-lg p-2 border border-neutral-100" data-id="element-196">
              <div className="flex justify-center gap-1" data-id="element-197">
                {languages.map(lang => <motion.button key={lang.code} onClick={() => {
              setLanguage(lang.code);
              setIsLangMenuOpen(false);
            }} className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${language === lang.code ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'}`} whileTap={{
              scale: 0.95
            }} data-id="element-198">
                    {lang.label}
                  </motion.button>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-199">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="fixed inset-x-0 top-14 z-40 md:hidden px-4" data-id="element-200">
            <motion.div className="bg-white shadow-lg rounded-lg p-3 border border-neutral-100" initial={{
          scale: 0.98
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0.98
        }} data-id="element-201">
              <div className="flex flex-col gap-1" data-id="element-202">
                {navLinks.map((link, index) => <motion.div key={link.path} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} data-id="element-203">
                    <Link to={link.path} className={`block text-sm font-medium py-2 px-3 rounded-md transition-colors ${location.pathname === link.path ? 'bg-black text-white' : 'text-black hover:bg-neutral-100'}`} data-id="element-204">
                      {link.label}
                    </Link>
                  </motion.div>)}
                <motion.a href="tel:+34631012592" className="flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-full font-medium mt-2 text-sm" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.15
            }} whileTap={{
              scale: 0.98
            }} data-id="element-205">
                  <PhoneIcon className="w-3.5 h-3.5" data-id="element-206" />
                  +34 631 01 25 92
                </motion.a>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}